/*
  The checks on the admin's forms, in plain words. They help the admin before they press the button; the server checks
  everything again and has the last word (the list of common passwords, for one, is only on the server). The minimum
  length of a first password is a setting of the installation (12 by default); the browser is not told it, so
  it uses the default and the server's refusal is shown if the installation asks for more (see NOTES.md, contract requests).
*/

export interface Check {
  id: string;
  ok: boolean;
  text: string;
}

/** The default of `auth.password_min_length`. */
export const FIRST_PASSWORD_MIN = 12;

/** A discipline's code: `^[a-z][a-z0-9_]{1,31}$` (2.1.1). */
export const WORKSPACE_CODE = /^[a-z][a-z0-9_]{1,31}$/;

export const USERNAME_MAX = 64;

export interface NewAccount {
  username: string;
  displayName: string;
  firstPassword: string;
}

export interface Result {
  checks: Check[];
  /** Every check passed: the button may be pressed. */
  ok: boolean;
}

function finish(checks: Check[]): Result {
  return { checks, ok: checks.every((check) => check.ok) };
}

/** The checks on a new account. A first password is optional; when one is typed it must be long enough. */
export function checkNewAccount(values: NewAccount, existingUsernames: readonly string[]): Result {
  const username = values.username.trim();
  const taken = existingUsernames.some((name) => name.toLowerCase() === username.toLowerCase());
  const checks: Check[] = [
    { id: "username", ok: username.length > 0 && !/\s/.test(username) && username.length <= USERNAME_MAX, text: "The user name is filled in, has no spaces and is at most 64 characters." },
    { id: "unique", ok: username.length === 0 || !taken, text: "No other account uses this user name." },
    { id: "name", ok: values.displayName.trim().length > 0, text: "The name shown on screen is filled in." },
  ];
  if (values.firstPassword.length > 0) {
    checks.push({ id: "length", ok: values.firstPassword.length >= FIRST_PASSWORD_MIN, text: `The first password has at least ${FIRST_PASSWORD_MIN} characters.` });
    checks.push({ id: "different", ok: username.length === 0 || values.firstPassword.toLowerCase() !== username.toLowerCase(), text: "The first password is not the user name." });
  }
  return finish(checks);
}

/** The checks on a first password that resets an account. */
export function checkFirstPassword(password: string, username: string): Result {
  return finish([
    { id: "length", ok: password.length >= FIRST_PASSWORD_MIN, text: `The first password has at least ${FIRST_PASSWORD_MIN} characters.` },
    { id: "different", ok: password.toLowerCase() !== username.toLowerCase(), text: "The first password is not the user name." },
  ]);
}

/** The checks on a new discipline: a code that nobody (open or closed) uses, and a name. */
export function checkNewDiscipline(code: string, name: string, existingCodes: readonly string[]): Result {
  const trimmed = code.trim();
  return finish([
    { id: "code", ok: WORKSPACE_CODE.test(trimmed), text: "The code starts with a lower-case letter and has 2 to 32 lower-case letters, digits or underscores." },
    { id: "unique", ok: trimmed.length === 0 || !existingCodes.includes(trimmed), text: "No discipline, open or closed, uses this code." },
    { id: "name", ok: name.trim().length > 0, text: "The name is filled in." },
  ]);
}
